import React, { useEffect, useMemo } from "react";

function decodeHtml(html) {
    if (!html || typeof document === "undefined") {
        return html || "";
    }

    const textArea = document.createElement("textarea");
    textArea.innerHTML = html;

    return textArea.value;
}

export default function ArticleBody({ post }) {

    const content = useMemo(() => {
        return decodeHtml(
            post?.content ||
            post?.content_html ||
            ""
        );
    }, [
        post?.content,
        post?.content_html
    ]);


    /*
    |--------------------------------------------------------------------------
    | Check whether this article contains the special
    | left sidebar + right article layout.
    |--------------------------------------------------------------------------
    |
    | Normal article:
    |
    | <article>
    |     ...
    | </article>
    |
    |
    | Blog layout article:
    |
    | <aside class="blog-test-toc">
    |     ...
    | </aside>
    |
    | <article class="blog-test-article">
    |     ...
    | </article>
    |
    |--------------------------------------------------------------------------
    */
    const hasBlogLayout =
        content.includes('class="blog-test-toc"') &&
        content.includes('class="blog-test-article"');


    useEffect(() => {

        /*
        |--------------------------------------------------------------------------
        | Don't run TOC logic for normal articles
        |--------------------------------------------------------------------------
        */

        if (!hasBlogLayout || !content) {
            return;
        }


        /*
        |--------------------------------------------------------------------------
        | Find the CURRENT article wrapper
        |--------------------------------------------------------------------------
        |
        | Instead of document.querySelector(), create a temporary
        | container and then find the actual rendered container.
        |--------------------------------------------------------------------------
        */

        const container =
            document.querySelector(
                ".article-body.blog-test-layout"
            );


        if (!container) {
            return;
        }

        const faqContainers = Array.from(
            container.querySelectorAll(".blog-test-faq")
        );

        const handleFaqClick = (event) => {
            const summary = event.target.closest("summary");
            const details = summary?.parentElement;

            if (!details || details.tagName !== "DETAILS") {
                return;
            }

            faqContainers.forEach((faq) => {
                faq.querySelectorAll("details[open]").forEach((openItem) => {
                    if (openItem !== details) {
                        openItem.open = false;
                    }
                });
            });
        };

        faqContainers.forEach((faq) => {
            const initiallyOpen = faq.querySelector("details[open]");
            faq.querySelectorAll("details[open]").forEach((openItem) => {
                if (openItem !== initiallyOpen) {
                    openItem.open = false;
                }
            });
            faq.addEventListener("click", handleFaqClick);
        });


        /*
        |--------------------------------------------------------------------------
        | Find TOC links
        |--------------------------------------------------------------------------
        */

        const tocLinks = Array.from(
            container.querySelectorAll(
                ".blog-test-toc a[href^='#']"
            )
        );


        /*
        |--------------------------------------------------------------------------
        | Find article sections
        |--------------------------------------------------------------------------
        */

        const sections = Array.from(
            container.querySelectorAll(
                ".blog-test-article .blog-test-section[id]"
            )
        );


        if (
            !tocLinks.length ||
            !sections.length
        ) {
            return;
        }


        /*
        |--------------------------------------------------------------------------
        | Set active TOC link
        |--------------------------------------------------------------------------
        */

        const setActiveLink = (sectionId) => {

            tocLinks.forEach((link) => {

                const href =
                    link.getAttribute("href");

                const linkId =
                    href
                        ? href.substring(1)
                        : "";

                link.classList.toggle(
                    "is-active",
                    linkId === sectionId
                );

            });

        };


        /*
        |--------------------------------------------------------------------------
        | TOC click
        |--------------------------------------------------------------------------
        |
        | IMPORTANT:
        |
        | We prevent the default anchor behaviour.
        |
        | Therefore:
        |
        | /blog#faq
        |
        | will NOT happen.
        |
        | URL remains:
        |
        | /blog
        |
        |--------------------------------------------------------------------------
        */

        const handleTocClick = (event) => {

            event.preventDefault();


            const link =
                event.currentTarget;


            const href =
                link.getAttribute("href");


            if (!href) {
                return;
            }


            const sectionId =
                href.substring(1);


            const section =
                container.querySelector(
                    `#${CSS.escape(sectionId)}`
                );


            if (!section) {
                return;
            }


            /*
            |--------------------------------------------------------------------------
            | Immediately make clicked item active
            |--------------------------------------------------------------------------
            */

            setActiveLink(sectionId);


            /*
            |--------------------------------------------------------------------------
            | Header offset
            |--------------------------------------------------------------------------
            |
            | Change this value according to your
            | actual Header height.
            |--------------------------------------------------------------------------
            */

            const headerOffset = 100;


            const sectionTop =
                section.getBoundingClientRect().top +
                window.scrollY;


            const scrollTop =
                sectionTop -
                headerOffset;


            window.scrollTo({

                top: scrollTop,

                behavior: "smooth",

            });

        };


        /*
        |--------------------------------------------------------------------------
        | Add TOC click listeners
        |--------------------------------------------------------------------------
        */

        tocLinks.forEach((link) => {

            link.addEventListener(
                "click",
                handleTocClick
            );

        });


        /*
        |--------------------------------------------------------------------------
        | Intersection Observer
        |--------------------------------------------------------------------------
        */

        const observer =
            new IntersectionObserver(
                (entries) => {

                    /*
                    |--------------------------------------------------------------------------
                    | Find visible sections
                    |--------------------------------------------------------------------------
                    */

                    const visibleSections =
                        entries
                            .filter(
                                (entry) =>
                                    entry.isIntersecting
                            )
                            .sort(
                                (a, b) =>
                                    a.boundingClientRect.top -
                                    b.boundingClientRect.top
                            );


                    /*
                    |--------------------------------------------------------------------------
                    | Update active section
                    |--------------------------------------------------------------------------
                    */

                    if (
                        visibleSections.length > 0
                    ) {

                        const activeSection =
                            visibleSections[0]
                                .target;


                        setActiveLink(
                            activeSection.id
                        );

                    }

                },
                {
                    /*
                    |--------------------------------------------------------------------------
                    | Intersection area
                    |--------------------------------------------------------------------------
                    |
                    | -120px:
                    | Ignore the fixed header area.
                    |
                    | -55%:
                    | Only consider the upper portion
                    | of the viewport.
                    |--------------------------------------------------------------------------
                    */

                    rootMargin:
                        "-120px 0px -55% 0px",

                    threshold: 0,

                }
            );


        /*
        |--------------------------------------------------------------------------
        | Observe sections
        |--------------------------------------------------------------------------
        */

        sections.forEach((section) => {

            observer.observe(section);

        });


        /*
        |--------------------------------------------------------------------------
        | Cleanup
        |--------------------------------------------------------------------------
        */

        return () => {

            tocLinks.forEach((link) => {

                link.removeEventListener(
                    "click",
                    handleTocClick
                );

            });

            faqContainers.forEach((faq) => {
                faq.removeEventListener("click", handleFaqClick);
            });

            observer.disconnect();

        };

    }, [
        content,
        hasBlogLayout
    ]);


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (!post) {

        return (
            <article className="article-body">

                <div className="skeleton">

                    <div
                        className="placeholder-line title"
                    />

                    <div
                        className="placeholder-line"
                    />

                    <div
                        className="placeholder-line"
                    />

                    <div
                        className="placeholder-line"
                    />

                    <div
                        className="placeholder-line"
                    />

                </div>

            </article>
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Normal article
    |--------------------------------------------------------------------------
    */

    if (!hasBlogLayout) {

        return (
            <div
                className="article-body"
                dangerouslySetInnerHTML={{
                    __html: content,
                }}
            />
        );

    }


    /*
    |--------------------------------------------------------------------------
    | Article with TOC/sidebar layout
    |--------------------------------------------------------------------------
    */

    return (
        <div
            className="article-body blog-test-layout"
            dangerouslySetInnerHTML={{
                __html: content,
            }}
        />
    );

}